import React, { useState } from "react";
import { useAuthStore } from "../state/authStore";
import { useSessionSocket } from "../hooks/useSessionSocket";

type TvScreen =
  | "Pairing"
  | "ProfileSelect"
  | "ChildHome"
  | "Library"
  | "Player"
  | "BreakTime";
type NavParams = Record<string, unknown>;

type Nav = {
  navigate: (screen: string, params?: NavParams) => void;
  replace: (screen: string, params?: NavParams) => void;
  goBack: () => void;
  // react-navigation API surface the child screens expect. TvApp is a
  // plain state machine, not a NavigationContainer, so these are shimmed.
  reset: (state: {
    index?: number;
    routes: { name: string; params?: NavParams }[];
  }) => void;
  setOptions: (opts: Record<string, unknown>) => void;
};

// Recover parentId from the JWT `sub` claim when the store predates the
// explicit parentId field — mirrors the self-heal in ProfileSelect.
function decodeJwtSub(token: string | null): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = typeof atob === "function" ? atob(padded) : "";
    const payload = JSON.parse(decoded);
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

export default function TvApp() {
  const { isAuthenticated, accessToken, parentId: storeParentId, tvId, logout } =
    useAuthStore();
  const [screen, setScreen] = useState<string>(
    isAuthenticated ? "ProfileSelect" : "Pairing",
  );
  const [params, setParams] = useState<NavParams>({});
  const [history, setHistory] = useState<
    { screen: string; params: NavParams }[]
  >([]);

  // Always-on TV socket so the parent can unpair this TV from any screen
  // (the per-screen sockets only exist during playback). On `unpaired` we
  // drop auth and return to the Pairing screen.
  const parentId = storeParentId ?? decodeJwtSub(accessToken);
  useSessionSocket({
    parentId: parentId ?? null,
    role: "tv",
    // Multi-TV: pass our tvId so the gateway buckets this socket under
    // (parentId, tvId). The parent's per-device Disconnect fires
    // forced_unpair targeted at this tvId — without this prop the TV
    // would bucket as 'legacy' and only the all-TVs unpair would
    // affect it.
    tvId: tvId ?? null,
    onParentCommand: (cmd) => {
      if (cmd.action === "unpaired" || cmd.action === "forced_unpair") {
        logout();
        setHistory([]);
        setParams({});
        setScreen("Pairing");
      }
    },
  });

  const nav: Nav = {
    navigate: (s, p = {}) => {
      setHistory((h) => [...h, { screen, params }]);
      setScreen(s);
      setParams(p);
    },
    replace: (s, p = {}) => {
      setScreen(s);
      setParams(p);
    },
    goBack: () => {
      const prev = history[history.length - 1];
      if (prev) {
        setScreen(prev.screen);
        setParams(prev.params);
        setHistory((h) => h.slice(0, -1));
      }
    },
    // Map react-navigation's reset to our state machine: jump to the last
    // route in the stack and clear history. Routes the TV doesn't own
    // (e.g. "ParentHome" from a mobile-only flow) just no-op here.
    reset: ({ routes }) => {
      const target = routes?.[routes.length - 1];
      if (!target) return;
      setHistory([]);
      setParams(target.params ?? {});
      setScreen(target.name);
    },
    // Native-stack header/gesture options don't apply to the web TV shell.
    setOptions: () => {},
  };

  const route = { params } as any;

  switch (screen) {
    case "Pairing": {
      const S = require("../screens/shared/PairingScreen").default;
      return <S navigation={nav} route={route} />;
    }
    case "ProfileSelect": {
      const S = require("../screens/child/ProfileSelectScreen").default;
      return <S navigation={nav} route={route} />;
    }
    case "ChildHome": {
      const S = require("../screens/child/ChildHomeScreen").default;
      return <S navigation={nav} route={route} />;
    }
    case "Library": {
      const S = require("../screens/child/LibraryScreen").default;
      return <S navigation={nav} route={route} />;
    }
    case "Player": {
      const S = require("../screens/child/PlayerScreen").default;
      return <S navigation={nav} route={route} />;
    }
    case "BreakTime": {
      const S = require("../screens/child/BreakTimeScreen").default;
      return <S navigation={nav} route={route} />;
    }
    default:
      return null;
  }
}
